/**
 * @fileoverview POSTWS handler for the "/:app_group_id/generative_ai/brand_guidelines_synthesis"
 * endpoint
 */
import fetch from "@lib/fetch";

import { appendFormDataValue } from "../formData";
import { reactQueryPostWebsocketAdapter } from "../reactQueryFetcherAdapters";
import { generativeAiAppGroupIdBrandGuidelinesSynthesis } from "./constants";
import type {
  PostwsGenerativeAiAppGroupIdBrandGuidelinesSynthesisPayload,
  PostwsGenerativeAiAppGroupIdBrandGuidelinesSynthesisProps,
} from "./types";
import { isPostwsGenerativeAiAppGroupIdBrandGuidelinesSynthesisResult } from "./validators";

export const postwsGenerativeAiAppGroupIdBrandGuidelinesSynthesis = reactQueryPostWebsocketAdapter(
  generativeAiAppGroupIdBrandGuidelinesSynthesis,
  async (
    signal,
    {
      appGroupId,
      brandPersonality,
      exclusions,
      sources,
      files,
      jobToken,
    }: PostwsGenerativeAiAppGroupIdBrandGuidelinesSynthesisProps
  ): Promise<{}> => {
    const hasFiles = Boolean(files?.length);
    const payload: PostwsGenerativeAiAppGroupIdBrandGuidelinesSynthesisPayload = {
      job_token: jobToken,
      brand_personality: brandPersonality,
      exclusions,
      // Multipart form-field flattening coerces every scalar to a string, so ship the sources
      // array as one JSON-encoded part when files are attached. The controller parses it back.
      sources: hasFiles ? JSON.stringify(sources) : sources,
    };

    if (!hasFiles) {
      return await fetch(
        `/${appGroupId}/generative_ai/brand_guidelines_synthesis`,
        "POST",
        payload,
        { signal },
        { propagateFetchRequestError: false }
      );
    }

    const body = new FormData();
    Object.entries(payload).forEach(([key, value]) => appendFormDataValue(body, key, value));
    // Each file becomes a files[] part the backend validates and encodes into an inline source.
    appendFormDataValue(body, "files", files);

    return await fetch(
      `/${appGroupId}/generative_ai/brand_guidelines_synthesis`,
      "POST",
      {},
      { signal, body },
      // setContentType: false lets the browser set the multipart boundary.
      { setContentType: false, propagateFetchRequestError: false }
    );
  },
  isPostwsGenerativeAiAppGroupIdBrandGuidelinesSynthesisResult
);
